import React, { useState } from "react";
import { Alert, Button, FormFeedback, Input, Spinner } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { updatePassword } from "../../helpers/backend_helper";
import HeaderSidebarToggle from "../../Components/Common/HeaderSidebarToggle";
import HomeLogoLink from "../../Components/Common/HomeLogoLink";

type StatusMessage = {
    type: "success" | "danger";
    message: string;
};

const ChangePassword = () => {
    const [status, setStatus] = useState<StatusMessage | null>(null);
    const [loading, setLoading] = useState(false);

    const validation = useFormik({
        initialValues: {
            current_password: "",
            password: "",
            password_confirmation: "",
        },
        validationSchema: Yup.object({
            current_password: Yup.string().required("Vui lòng nhập mật khẩu hiện tại"),
            password: Yup.string()
                .min(8, "Mật khẩu tối thiểu 8 ký tự")
                .required("Vui lòng nhập mật khẩu mới"),
            password_confirmation: Yup.string()
                .oneOf([Yup.ref("password")], "Mật khẩu xác nhận không khớp")
                .required("Vui lòng nhập lại mật khẩu mới"),
        }),
        onSubmit: async (values, { resetForm, setErrors }) => {
            setLoading(true);
            setStatus(null);
            try {
                await updatePassword({
                    current_password: values.current_password,
                    password: values.password,
                    password_confirmation: values.password_confirmation,
                });
                resetForm();
                setStatus({ type: "success", message: "Đổi mật khẩu thành công." });
            } catch (error: any) {
                const fieldErrors: Record<string, string> = {};
                const errors = error?.errors ?? error?.data?.errors;
                if (errors && typeof errors === "object") {
                    Object.entries(errors).forEach(([field, messages]) => {
                        if (Array.isArray(messages) && messages[0]) {
                            fieldErrors[field] = messages[0];
                        } else if (messages) {
                            fieldErrors[field] = String(messages);
                        }
                    });
                }

                if (Object.keys(fieldErrors).length > 0) {
                    setErrors(fieldErrors);
                }

                const fallbackMessage =
                    error?.message ||
                    error?.data?.message ||
                    "Không thể đổi mật khẩu. Vui lòng thử lại.";
                setStatus({ type: "danger", message: fallbackMessage });
            } finally {
                setLoading(false);
            }
        },
    });

    return (
        <React.Fragment>
            <div className="header-area" id="headerArea">
                <div className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <HomeLogoLink className="me-2" />
                    <div className="page-heading">
                        <h6 className="mb-0">Đổi mật khẩu</h6>
                    </div>
                    <HeaderSidebarToggle className="ms-2" />
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="container">
                    <div className="profile-wrapper-area py-3">
                        <div className="card user-data-card">
                            <div className="card-body">
                                {status && (
                                    <Alert color={status.type} className="mb-3">
                                        {status.message}
                                    </Alert>
                                )}
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        validation.handleSubmit();
                                    }}
                                >
                                    <div className="mb-3">
                                        <div className="title mb-2">
                                            <i className="mdi mdi-key-outline"></i>
                                            <span>Mật khẩu hiện tại</span>
                                        </div>
                                        <Input
                                            name="current_password"
                                            type="password"
                                            className="form-control"
                                            placeholder="Nhập mật khẩu hiện tại"
                                            value={validation.values.current_password}
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            invalid={
                                                !!(
                                                    validation.touched.current_password &&
                                                    validation.errors.current_password
                                                )
                                            }
                                        />
                                        {validation.touched.current_password &&
                                        validation.errors.current_password ? (
                                            <FormFeedback type="invalid">
                                                {validation.errors.current_password}
                                            </FormFeedback>
                                        ) : null}
                                    </div>
                                    <div className="mb-3">
                                        <div className="title mb-2">
                                            <i className="mdi mdi-lock-reset"></i>
                                            <span>Mật khẩu mới</span>
                                        </div>
                                        <Input
                                            name="password"
                                            type="password"
                                            className="form-control"
                                            placeholder="Nhập mật khẩu mới"
                                            value={validation.values.password}
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            invalid={
                                                !!(
                                                    validation.touched.password &&
                                                    validation.errors.password
                                                )
                                            }
                                        />
                                        {validation.touched.password && validation.errors.password ? (
                                            <FormFeedback type="invalid">
                                                {validation.errors.password}
                                            </FormFeedback>
                                        ) : null}
                                    </div>
                                    <div className="mb-3">
                                        <div className="title mb-2">
                                            <i className="mdi mdi-lock-check-outline"></i>
                                            <span>Nhập lại mật khẩu mới</span>
                                        </div>
                                        <Input
                                            name="password_confirmation"
                                            type="password"
                                            className="form-control"
                                            placeholder="Nhập lại mật khẩu mới"
                                            value={validation.values.password_confirmation}
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            invalid={
                                                !!(
                                                    validation.touched.password_confirmation &&
                                                    validation.errors.password_confirmation
                                                )
                                            }
                                        />
                                        {validation.touched.password_confirmation &&
                                        validation.errors.password_confirmation ? (
                                            <FormFeedback type="invalid">
                                                {validation.errors.password_confirmation}
                                            </FormFeedback>
                                        ) : null}
                                    </div>
                                    <Button
                                        type="submit"
                                        className="btn btn-primary btn-lg w-100"
                                        disabled={loading}
                                    >
                                        {loading && <Spinner size="sm" className="me-2" />}
                                        Cập nhật mật khẩu
                                    </Button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ChangePassword;
